import { useQuery } from '@apollo/client/react';
import { useEffect } from 'react';
import { Check } from 'react-bootstrap-icons';

import { buildPricingHref, ROUTES } from '@repo/common';
import {
  BillingPlanStatus,
  BillingPlanSummaryDocument,
  type BillingPlanSummaryQuery,
  DataTestIds,
} from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSSeparator } from '@repo/sds/SDSSeparator';
import { SDSText } from '@repo/sds/SDSText';
import { VerticalLogoWithTextIcon } from '@repo/ui/assets/icons/brandIcons/VerticalLogoWithTextIcon';
import { SettingsCard } from '@repo/ui/components/elements/settings-card/settings-card';

import { DisputeNotice } from './dispute-notice';
import { PaymentSummary } from './payment-summary';
import { RefundedPackageState } from './refund/refunded-package-state';

/** How often the package card re-reads while a refund is settling. */
const REFUND_POLL_INTERVAL_MS = 3000;

const PREMIUM_MBA_ENTITLEMENTS = ['All premium courses, every lesson', 'Live & archived workshops'] as const;

interface YourPackageSectionProps {
  isPortalLoading: boolean;
  onOpenPortal(): void;
  isReceiptLoading: boolean;
  onViewReceipt(): void;
}

/**
 * The one status this account's plan can be in.
 *
 * `loading`, `error`, and `no-plan` are mutually exclusive outcomes of the
 * same query, not independent toggles, so they are modeled as one
 * discriminated field instead of three separate booleans.
 */
type PackageStatus = 'loading' | 'error' | 'no-plan' | 'active';

function EntitlementList() {
  return (
    <SDSFlexbox className="PackageEntitlements" direction="column" gap="text-medium" alignItems="flex-start">
      {PREMIUM_MBA_ENTITLEMENTS.map(entitlement => (
        <SDSFlexbox key={entitlement} direction="row" gap="text-medium" alignItems="center">
          {/* A plain tick in the brand colour, as in the frame — not a circled one. */}
          <Check className="PackageEntitlements__tick" size={16} aria-hidden="true" />
          <SDSText
            type="body-medium"
            text={entitlement}
            color="text-secondary"
            dataTestId={DataTestIds.BILLING.PACKAGE_ENTITLEMENT}
          />
        </SDSFlexbox>
      ))}
    </SDSFlexbox>
  );
}

/**
 * The Premium MBA plan this account holds, and how much of it has been paid.
 *
 * "View receipt" and the amount facts here now have a real source: unlike the
 * account-access read this replaced, `myBillingPlanSummary` resolves an
 * actual settled purchase, so the label promises only what is true.
 */
export function YourPackageSection({
  isPortalLoading,
  onOpenPortal,
  isReceiptLoading,
  onViewReceipt,
}: YourPackageSectionProps) {
  const { data, loading, error, startPolling, stopPolling } = useQuery<BillingPlanSummaryQuery>(
    BillingPlanSummaryDocument,
    { fetchPolicy: 'cache-and-network' },
  );
  const plan = data?.myBillingPlanSummary ?? null;
  const isRefundPending = plan?.status === BillingPlanStatus.REFUND_PENDING;

  // A refund is accepted at once but only settles when the provider confirms
  // it, so while one is in flight the page checks back until it lands. Only
  // then: an account with nothing pending is not polled.
  useEffect(() => {
    if (!isRefundPending) {
      return undefined;
    }
    startPolling(REFUND_POLL_INTERVAL_MS);
    return stopPolling;
  }, [isRefundPending, startPolling, stopPolling]);

  // Error before not-found: a failed read has not established that this
  // account holds no plan.
  const status: PackageStatus = loading && !data ? 'loading' : error ? 'error' : plan ? 'active' : 'no-plan';

  return (
    <SettingsCard direction="column" dataTestId={DataTestIds.BILLING.PACKAGE_SECTION}>
      <SDSFlexbox direction="column" gap="no-design-24" fullWidth>
        <SDSText text="Your Package" type="h5" />
        <PackageContent
          status={status}
          plan={plan}
          isPortalLoading={isPortalLoading}
          onOpenPortal={onOpenPortal}
          isReceiptLoading={isReceiptLoading}
          onViewReceipt={onViewReceipt}
        />
      </SDSFlexbox>
    </SettingsCard>
  );
}

function PackageContent({
  status,
  plan,
  isPortalLoading,
  onOpenPortal,
  isReceiptLoading,
  onViewReceipt,
}: YourPackageSectionProps & {
  status: PackageStatus;
  plan: BillingPlanSummaryQuery['myBillingPlanSummary'];
}) {
  if (status === 'loading') {
    return (
      <SDSText
        type="body-medium"
        text="Checking your plan…"
        color="text-secondary"
        dataTestId={DataTestIds.BILLING.PACKAGE_LOADING_STATE}
      />
    );
  }

  if (status === 'error') {
    return (
      <SDSText
        type="body-medium"
        text="Your plan could not be loaded right now."
        color="alert"
        dataTestId={DataTestIds.BILLING.PACKAGE_ERROR_STATE}
      />
    );
  }

  if (status === 'no-plan' || !plan) {
    return (
      <SDSFlexbox
        direction="column"
        gap="text-medium"
        alignItems="flex-start"
        dataTestId={DataTestIds.BILLING.PACKAGE_EMPTY_STATE}
      >
        <SDSText type="body-medium" text="No plan is active on this account yet." color="text-secondary" />
        <SDSButton text="Get the MBA" size="small" href={buildPricingHref({ returnTo: ROUTES.BILLING })} />
      </SDSFlexbox>
    );
  }

  // Asked for but not yet confirmed: the plan has not been revoked, so it must
  // not read as refunded, and it must not offer the refund a second time.
  if (plan.status === BillingPlanStatus.REFUND_PENDING) {
    return (
      <SDSFlexbox
        direction="column"
        gap="text-medium"
        alignItems="flex-start"
        dataTestId={DataTestIds.BILLING.REFUND.PENDING_STATE}
      >
        <SDSText type="h4" text="Refund in progress" />
        <SDSText
          type="body-large"
          color="text-secondary"
          text={`Your refund for ${plan.productTitle} has been submitted. This page will update as soon as it is confirmed, and your access will end then.`}
        />
      </SDSFlexbox>
    );
  }

  // A refunded purchase keeps its card but loses its plan, so it renders its
  // own state rather than an entitlement list for access that has ended.
  if (plan.status === BillingPlanStatus.REFUNDED) {
    return <RefundedPackageState plan={plan} isReceiptLoading={isReceiptLoading} onViewReceipt={onViewReceipt} />;
  }

  return (
    // 24px between blocks, the frame's content/regular gap. SDS has no token for
    // it, so these use no-design-24 rather than content-default's 28.
    <SDSFlexbox direction="column" gap="no-design-24" alignItems="flex-start" fullWidth>
      {plan.dispute ? <DisputeNotice dispute={plan.dispute} productTitle={plan.productTitle} /> : null}

      <SDSFlexbox direction="row" gap="no-design-16" alignItems="center" fullWidth>
        <SDSFlexbox className="PackageLogo" alignItems="center" justifyContent="center">
          <VerticalLogoWithTextIcon />
        </SDSFlexbox>
        <SDSFlexbox direction="column" alignItems="flex-start">
          <SDSText type="h4" text={plan.productTitle} dataTestId={DataTestIds.BILLING.PACKAGE_TITLE} />
          <EntitlementList />
        </SDSFlexbox>
      </SDSFlexbox>

      <SDSSeparator />

      <PaymentSummary
        plan={plan}
        isPortalLoading={isPortalLoading}
        onOpenPortal={onOpenPortal}
        isReceiptLoading={isReceiptLoading}
        onViewReceipt={onViewReceipt}
      />
    </SDSFlexbox>
  );
}
